import React, { useState } from 'react';
import { View, StyleSheet } from 'react-native';

import DataStore from '../../support/DataStore';
import getAppVersion from '../../support/helpers/getAppVersion';

import { Button, DropdownButton, Text } from '../uiold';

import LoginAsDialog from './LoginAsDialogMenu';
import { RouterContext } from '../Navigation';

import { Account } from '../../types/shared/Account';
import { User } from '../../types/shared/User';

import { useReleaseNotes } from '../../components/ReleaseNotesProvider';

type Props = {
  account?: Account;
  onChangePasswordPress: () => void;
  user?: User;
};

const dataStore = DataStore.getInstance('app');

function UserNav(props: Props) {
  let { account, onChangePasswordPress, user } = props;
  let [showLoginAsDialog, setShowLoginAsDialog] = useState(false);
  const { showReleaseNotes } = useReleaseNotes();

  if (!account || !user) {
    return null;
  }
  let isAdmin = account.system_admin;

  return (
    <View style={styles.root}>
      <View style={styles.buttonContainer}>
        {isAdmin && (
          <View style={styles.adminAccount}>
            <Text style={{ fontSize: 12 }}>
              {t('Logged in as {name}', { name: account.name })}
            </Text>
          </View>
        )}
        <DropdownButton
          testID="button-nav-dropdown-list"
          title={user.name}
          showCaret
          noBorder
          alignPickerRight
        >
          <View style={styles.accountName}>
            <Text style={styles.accountNameText}>{account.name}</Text>
          </View>
          <View style={styles.buttons}>
            <Button
              title={t('Change Password')}
              iconName="lock"
              iconColor="#333"
              onPress={onChangePasswordPress}
              style={styles.button}
              noBorder
            />
            {renderLoginAsButton()}
            <RouterContext.Consumer>
              {(context) => (
                <Button
                  title="Logout"
                  testID="button-nav-dropdown-logout"
                  iconName="logout"
                  iconColor="#333"
                  onPress={() => {
                    // Set current route to '/' before logging out
                    context && context.replaceRoute('/');
                    logout();
                  }}
                  style={styles.button}
                  noBorder
                />
              )}
            </RouterContext.Consumer>
            <Button
              title={t('Show Release Notes')}
              iconName="tag"
              iconColor="#333"
              onPress={() => showReleaseNotes()}
              style={styles.button}
              noBorder
            />
            <View style={styles.appVersion}>
              <Text style={styles.appVersionText}>{`v${getAppVersion()}`}</Text>
            </View>
          </View>
        </DropdownButton>
      </View>
      {renderLoginAsDialog()}
    </View>
  );

  function renderLoginAsButton() {
    let { account } = props;
    if (account && !account.system_admin) {
      return;
    }
    return (
      <Button
        title={t('Login As')}
        iconName="person"
        iconColor="#333"
        onPress={() => setShowLoginAsDialog(true)}
        style={styles.button}
        noBorder
      />
    );
  }

  function renderLoginAsDialog() {
    let { account } = props;
    if (!showLoginAsDialog || !account) {
      return;
    }
    return (
      <LoginAsDialog
        accountId={account.id}
        onHide={() => setShowLoginAsDialog(false)}
      />
    );
  }
}

function logout() {
  // clearAll doesn't fire change methods, so we explicitly logout first
  dataStore.set('user', null);
  dataStore.set('account', null);
  dataStore.clearAll();
}

let styles = StyleSheet.create({
  root: {
    flexDirection: 'row',
    marginHorizontal: 2,
    height: 34,
    alignItems: 'center',
  },
  buttonContainer: {
    height: 34,
    flexDirection: 'row',
  },
  buttons: {
    alignItems: 'flex-start',
    flex: 1,
  },
  button: {
    alignSelf: 'stretch',
  },
  accountName: { paddingVertical: 5, paddingHorizontal: 14 },
  adminAccount: { marginTop: 10, paddingHorizontal: 14 },
  accountNameText: {
    color: 'grey',
    fontSize: 13,
  },
  appVersion: {
    paddingVertical: 5,
    paddingHorizontal: 14,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  appVersionText: {
    color: 'grey',
    fontSize: 11,
  },
});

export default UserNav;
